import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { I18nextProvider } from 'react-i18next';
// i18nConfig and enCommon are no longer needed here
import i18n, { SUPPORTED_LANGUAGES } from './lib/i18n';

// Context now only needs to provide the current language
type LanguageContextType = {
  currentLanguage: string;
};

const LanguageContext = createContext<LanguageContextType>({
  currentLanguage: 'en',
});

export const useLanguageContext = () => useContext(LanguageContext);

export function I18nProvider({ children }: { children: ReactNode }) {
  // i18n instance is initialized synchronously in app/lib/i18n.ts
  // We only need state for the current language, derived from the i18n instance
  const [currentLanguage, setCurrentLanguage] = useState(
    i18n.language?.split('-')[0] || 'en'
  );
  // console.log('[I18nProvider] Rendering...', {
  //   currentLanguage: currentLanguage, // Use state variable
  //   initialI18nLang: i18n.language, // Log initial instance lang
  // });

  // This useEffect runs once on the client after initial render
  useEffect(() => {
    // console.log(
    //   '%c[I18nProvider] Client-side useEffect START',
    //   'color: green; font-weight: bold;'
    // );

    // --- Event Listener Setup (Moved Up) ---
    const handleLanguageChange = (lng: string | undefined) => {
      const newLang = (lng || 'en').split('-')[0];
      // console.log(
      //   `%c[I18nProvider] EVENT: languageChanged detected! New language: ${newLang}`,
      //   'color: blue; font-weight: bold;'
      // );
      // Update state and html lang attribute only if it actually changed
      // Check against the *current* state value at the time the event fires
      setCurrentLanguage((prevLang) => {
        if (newLang !== prevLang) {
          // console.log(
          //   `[I18nProvider] EVENT: Updating state and html lang to ${newLang}`
          // );
          document.documentElement.lang = newLang;
          return newLang;
        }
        return prevLang;
      });
    };

    // Listen for language changes triggered by i18n.changeLanguage or detectors
    // console.log('[I18nProvider] Attaching languageChanged listener.');
    i18n.on('languageChanged', handleLanguageChange);

    // --- Client-side Language Detection ---
    const storedLang = localStorage.getItem('i18nextLng');
    // console.log(`[I18nProvider] Read localStorage 'i18nextLng': ${storedLang}`);
    let detectedLang = storedLang || 'en';
    const supportedCodes = SUPPORTED_LANGUAGES.map((lang) => lang.code);
    if (!supportedCodes.includes(detectedLang.split('-')[0])) {
      // console.log(
      //   `[I18nProvider] Detected lang ${detectedLang} not supported, falling back to 'en'.`
      // );
      detectedLang = 'en';
    }
    const targetLang = detectedLang.split('-')[0];
    // console.log(`[I18nProvider] Determined targetLang: ${targetLang}`);

    // Set the document lang attribute immediately based on the determined target language
    document.documentElement.lang = targetLang;
    // console.log(`[I18nProvider] Set documentElement.lang to ${targetLang}`);

    // Get the language i18n was initialized with (server or initial client)
    const initialRenderLang = i18n.language?.split('-')[0] || 'en';
    // console.log(
    //   `[I18nProvider] Initial render language (from i18n instance): ${initialRenderLang}`
    // );

    // Change i18next language state only if detected localStorage lang differs from initial render lang
    if (targetLang !== initialRenderLang) {
      // console.log(
      //   `%c[I18nProvider] CONDITION MET: targetLang (${targetLang}) !== initialRenderLang (${initialRenderLang}). Attempting i18next state change.`,
      //   'color: orange;'
      // );
      // Trigger i18next change with a delay
      setTimeout(() => {
        // console.log(
        //   `[I18nProvider] Calling i18n.changeLanguage(${targetLang}) after delay...`
        // );
        i18n.changeLanguage(targetLang).then(() => {
          // console.log(
          //   `[I18nProvider] i18n.changeLanguage(${targetLang}) promise resolved after delay.`
          // );
        });
        // .catch((error) => {
        //   // console.error(
        //   //   `[I18nProvider] Error during delayed i18n.changeLanguage(${targetLang}):`,
        //   //   error
        //   // );
        // });
      }, 50); // 50ms delay
    } else {
      // If targetLang matches initialRenderLang, ensure the React state also matches, if it somehow diverged.
      // The document lang is already set correctly above.
      if (initialRenderLang !== currentLanguage) {
        // console.log(
        //   `[I18nProvider] Aligning React state (${currentLanguage}) with initial/target language (${initialRenderLang}).`
        // );
        setCurrentLanguage(initialRenderLang);
        // No need to set document.documentElement.lang here, already done.
      }
      // console.log(
      //   '[I18nProvider] CONDITION NOT MET: targetLang matches initialRenderLang. No i18next state change needed.'
      // );
    }

    // console.log(
    //   '%c[I18nProvider] Client-side useEffect END',
    //   'color: green; font-weight: bold;'
    // );

    // --- Cleanup ---
    return () => {
      // console.log('[I18nProvider] Cleanup: Removing languageChanged listener.');
      i18n.off('languageChanged', handleLanguageChange);
    };
    // Empty dependency array ensures this effect runs only once on mount
  }, []);

  // Memoize context value based on currentLanguage state
  const contextValue = useMemo(() => {
    return {
      currentLanguage,
    };
  }, [currentLanguage]);

  // Render children immediately. i18n instance is guaranteed to exist.
  // console.log(
  //   '[I18nProvider] Rendering children. Current Lang State:',
  //   currentLanguage
  // );

  // I18nextProvider passes the i18n instance down via React context
  // LanguageContext provides our simplified language state
  return (
    <I18nextProvider i18n={i18n}>
      <LanguageContext.Provider value={contextValue}>
        {children}
      </LanguageContext.Provider>
    </I18nextProvider>
  );
}
